import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaExclamation } from "react-icons/fa";
import FailedMsg from "../../../Extra/FailedMsg";
import SuccessMsg from "../../../Extra/SuccessMsg";
import SendWhatsappMsg from "../../../Extra/SendWhatsappMsg";

const urlPrefix = process.env.REACT_APP_URL_PREFIX;

const initialFormData = {
  type: "",
  msg_sender: "",
  subject: "",
  date: "",
  message: "",
  remainder_date: "",
  document: [],
};

const normalizeDate = (value) => {
  if (!value) return "";

  const cleaned = value.replace(/\*/g, "").trim();

  let match = cleaned.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);

  if (match) {
    const [, day, month, year] = match;
    const d = new Date(+year, +month - 1, +day);

    if (
      d.getFullYear() !== +year ||
      d.getMonth() !== +month - 1 ||
      d.getDate() !== +day
    ) {
      return "";
    }

    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  match = cleaned.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

  if (match) {
    const [, year, month, day] = match;
    const d = new Date(+year, +month - 1, +day);

    if (
      d.getFullYear() !== +year ||
      d.getMonth() !== +month - 1 ||
      d.getDate() !== +day
    ) {
      return "";
    }

    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return "";
};

const extractBetween = (html, startString, endString) => {
  if (!html) return "";

  const temp = document.createElement("div");
  temp.innerHTML = html;

  temp.querySelectorAll("br").forEach((el) => {
    el.replaceWith(" ");
  });

  temp.querySelectorAll("p, div").forEach((el) => {
    el.insertAdjacentText("afterend", " ");
  });

  const text = (temp.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const startIndex = startString ? text.indexOf(startString) : 0;

  if (startIndex === -1) return "";

  const contentStart = startIndex + startString.length;
  const endIndex = endString
    ? text.indexOf(endString, contentStart)
    : text.length;

  if (endIndex === -1) return "";

  return text.substring(contentStart, endIndex).trim();
};

const cleanSender = (value) =>
  (value || "")
    .trim()
    .replace(/^[:\-\s]+/, "")
    .replace(/\s+/g, " ");

const FormInputFormMsg = ({ data }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [show, setShow] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Allows clearing the real browser file input.
  const fileInputRef = useRef(null);

  const clearSelectedFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setFormData((prev) => ({
      ...prev,
      document: [],
    }));
  };

  // Auto-fill message fields without overwriting reminder_date
  // or a file selected by the user.
  useEffect(() => {
    if (!data) {
      setFormData((prev) => ({
        ...prev,
        msg_sender: "",
        subject: "",
        date: "",
        remainder_date: "",
        message: "",
        // Keep the reminder date independent.
      }));
      return;
    }

    const from =
      extractBetween(data, "MESSAGE: FROM", "TO") ||
      extractBetween(data, "FROM", "TO");

    const cc = extractBetween(data, "CC-", "DATE");
    const rm = extractBetween(data, "RM-", "DATE");
    const ecw = extractBetween(data, "MESSAGE NO.", "DATE");

    const dateText =
      extractBetween(data, "DATE", "SUBJECT") ||
      extractBetween(data, "DATE", "");

    const dateMatch = dateText.match(
      /\b\d{1,2}[./-]\d{1,2}[./-]\d{4}\b|\b\d{4}-\d{1,2}-\d{1,2}\b/,
    );

    const extractedDate = normalizeDate(dateMatch?.[0]);

    let subject = "";

    if (ecw) {
      subject = ecw.replace(/\./g, "").trim();
    } else if (cc) {
      subject = `CC-${cc.trim().slice(0, 3)}`;
    } else if (rm) {
      subject = `RM-${rm.trim().slice(0, 3)}`;
    }

    setFormData((prev) => ({
      ...prev,
      msg_sender: cleanSender(from),
      subject,
      date: extractedDate,
      message: data,
      remainder_date: prev.remainder_date,
      document: prev.document,
    }));
  }, [data]);

  // const handleChange = (e) => {
  //   const { name, value, files } = e.target;

  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: name === "document" ? Array.from(files || []) : value,
  //   }));
  // };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "document") {
      const selectedFiles = Array.from(files || []);
      const validFiles = [];

      for (const file of selectedFiles) {
        if (file.size > MAX_FILE_SIZE) {
          alert(`"${file.name}" exceeds the 10 MB file size limit.`);
        } else {
          validFiles.push(file);
        }
      }

      setFormData((prev) => ({
        ...prev,
        document: [...(prev.document || []), ...validFiles],
      }));

      // Allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({ ...initialFormData });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setResult(null);
    setShow(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setShow(false);
    setResult(null);
    setLoading(true);

    try {
      // Create multipart/form-data payload
      const payload = new FormData();

      payload.append("type", formData.type || "");
      payload.append("msg_sender", formData.msg_sender || "");
      payload.append("subject", formData.subject || "");
      payload.append("date", formData.date || "");
      payload.append("reminder_date", formData.remainder_date || "");
      payload.append("message", formData.message || "");

      // Upload the file using the exact field name expected by the API
      const selectedFiles = formData.document || [];

      selectedFiles.forEach((file) => {
        payload.append("documents", file);
      });

      // Send multipart/form-data to the API
      const response = await axios.post(
        `${urlPrefix}msg/insert_messages`,
        payload,
      );

      // Reset the form after successful submission
      setFormData({ ...initialFormData });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setResult({
        success: true,
        message: response.data?.message || "Message saved successfully.",
      });

      setShow(true);
    } catch (error) {
      console.error("Submission error:", error);

      setResult({
        success: false,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save the message.",
      });

      setShow(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <div
        className="card shadow-sm border-0 mx-auto"
        style={{ maxWidth: "850px" }}
      >
        <div className="card-header bg-primary text-white py-3">
          <h5 className="mb-1">Create Message</h5>
          <small>
            <FaExclamation className="me-1" />
            Please verify auto-filled data before submitting.
          </small>
        </div>

        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Type <span className="text-danger">*</span>
                </label>
                <select
                  name="type"
                  className="form-select"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select type
                  </option>
                  <option value="ICMS">ICMS</option>
                  <option value="FOIS">FOIS</option>
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  From <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="msg_sender"
                  className="form-control"
                  placeholder="Enter sender name or office"
                  value={formData.msg_sender}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Subject <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  className="form-control"
                  placeholder="Enter message subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">
                  Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  className="form-control"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">Reminder Date</label>
                <input
                  type="date"
                  name="remainder_date"
                  className="form-control"
                  value={formData.remainder_date}
                  onChange={handleChange}
                />
                <small className="text-muted">Optional follow-up date.</small>
              </div>

              <div className="col-12">
                {/* <label className="form-label fw-semibold">
                  Message <span className="text-danger">*</span>
                  <small className="text-secondary ms-2">(HTML format)</small>
                </label> */}

                {/* <textarea
                  name="message"
                  className="form-control"
                  rows="5"
                  placeholder="Enter your HTML message here..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                /> */}

                {/* HTML Preview */}
                <div className="mt-3">
                  <label className="form-label fw-semibold">
                    Message Preview<span className="text-danger">*</span>
                  </label>

                  <div
                    className="border rounded-3 p-3 bg-white"
                    // style={{
                    //   minHeight: "120px",
                    //   overflowWrap: "anywhere",
                    // }}
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                    dangerouslySetInnerHTML={{
                      __html:
                        formData.message ||
                        "<p class='text-muted mb-0'>Message preview will appear here...</p>",
                    }}
                  />
                </div>
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">
                  Attach File (PDF or Image)
                </label>

                <input
                  ref={fileInputRef}
                  type="file"
                  name="document"
                  className="form-control"
                  accept=".pdf,.doc,.docx,.docm,.dot,.dotx,.dotm,.xls,.xlsx,.xlsm,.xlsb,.xlt,.xltx,.xltm,.csv,.ods,image/*"
                  multiple
                  onChange={handleChange}
                  disabled={loading}
                />

                <div className="mt-2">
                  <small className="text-muted">
                    Supported formats: PDF, JPG, PNG, WEBP and other
                    browser-supported images. Total size:{" "}
                  </small>
                  <small className="">
                    {(
                      (formData.document || []).reduce(
                        (total, file) => total + file.size,
                        0,
                      ) /
                      (1024 * 1024)
                    ).toFixed(2)}{" "}
                    MB
                  </small>
                </div>

                {formData.document.length > 0 && (
                  <div
                    className="row mt-2"
                    style={{
                      maxHeight: "150px",
                      height: "150px",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    {formData.document.map((file, index) => (
                      <div
                        className={`${formData.document.length === 1 ? "col-md" : "col-md-6"}`}
                      >
                        <div
                          key={`${file.name}-${index}`}
                          style={{ fontSize: "12px" }}
                          className={`mt- m-1  bg-light rounded d-flex justify-content-between align-items-center`}
                        >
                          <span className="text-break ps-1">
                            {index + 1}
                            <i className="bi bi-paperclip ms-2 me-2" />
                            {file.name}
                          </span>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger flex-shrink-0"
                            disabled={loading}
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                document: prev.document.filter(
                                  (_, i) => i !== index,
                                ),
                              }));

                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-12 d-flex justify-content-end gap-2 mt-4">
                {data?.length > 0 && data !== "<p></p>" && (
                  <SendWhatsappMsg
                    defaultNumber=""
                    defaultMessage={data}
                    defaultAttachments={[]}
                  />
                )}

                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Reset
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving...
                    </>
                  ) : (
                    "save Message"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="p-3">
        {result?.success ? (
          <SuccessMsg
            type="Success"
            message={result.message}
            show={show}
            setShow={setShow}
          />
        ) : result ? (
          <FailedMsg
            type="Failed"
            message={result.message}
            show={show}
            setShow={setShow}
          />
        ) : null}
      </div>
    </div>
  );
};

export default FormInputFormMsg;
